import { AnalyticsType, Exchange } from '@/types/generated/common/types'
import type { DataPayload } from '@/types/generated/public_api'

export const DEFAULT_AMBERDATA_EXCHANGE = Exchange.EXCHANGE_DERIBIT

export const AMBERDATA_ANALYTICS_TYPES = [
  AnalyticsType.ANALYTICS_TYPE_VOL_METRICS_24HR,
  AnalyticsType.ANALYTICS_TYPE_PUT_CALL_RATIO,
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_FLOATING,
  AnalyticsType.ANALYTICS_TYPE_IMPLIED_VS_REALIZED,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_FLOATING,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_RICHNESS,
  AnalyticsType.ANALYTICS_TYPE_VOLATILITY_INDEX,
  AnalyticsType.ANALYTICS_TYPE_SKEW_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_SKEW_FLOATING,
  // ENG-738 §7.2
  AnalyticsType.ANALYTICS_TYPE_USD_GAMMA,
  AnalyticsType.ANALYTICS_TYPE_GEX,
  AnalyticsType.ANALYTICS_TYPE_VOL_CONES,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_DOW,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_MOY,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_HOUR,
  // ENG-941 / oems#260
  AnalyticsType.ANALYTICS_TYPE_RETURNS_DOW,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_HOUR,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION_CUMULATIVE,
] as const

export type AmberdataAnalyticsType = (typeof AMBERDATA_ANALYTICS_TYPES)[number]

export const AMBERDATA_RANGE_ANALYTICS_TYPES = [
  AnalyticsType.ANALYTICS_TYPE_PUT_CALL_RATIO,
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_FLOATING,
  AnalyticsType.ANALYTICS_TYPE_IMPLIED_VS_REALIZED,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_RICHNESS,
  AnalyticsType.ANALYTICS_TYPE_VOLATILITY_INDEX,
  // Skew is computed on read from the delta surface — range-only, never snapshotted or streamed.
  AnalyticsType.ANALYTICS_TYPE_SKEW_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_SKEW_FLOATING,
  // USD Gamma has genuine deep history — get_analytic_range with from/to works
  // as expected, same as Put/Call Ratio.
  AnalyticsType.ANALYTICS_TYPE_USD_GAMMA,
  // Computed on read from the chart MVs, genuine deep range history — same
  // shape as USD Gamma, not RV Seasonality (which is snapshot-only).
  AnalyticsType.ANALYTICS_TYPE_RETURNS_DOW,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_HOUR,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION_CUMULATIVE,
] as const

export type AmberdataRangeAnalyticsType = (typeof AMBERDATA_RANGE_ANALYTICS_TYPES)[number]

export const AMBERDATA_SNAPSHOT_ONLY_ANALYTICS_TYPES = [
  AnalyticsType.ANALYTICS_TYPE_VOL_METRICS_24HR,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_FLOATING,
  // GEX/Vol Cones/RV seasonality do accrue range history server-side, but each
  // is naturally a "latest snapshot" visualization (a chain snapshot, a cone,
  // a weekday/month/hour bar chart) — browsing their history over time is a valid
  // future enhancement, not needed for this pass.
  AnalyticsType.ANALYTICS_TYPE_GEX,
  AnalyticsType.ANALYTICS_TYPE_VOL_CONES,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_DOW,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_MOY,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_HOUR,
] as const

// Types that show two date pickers for snapshot comparison (current vs historical)
export const AMBERDATA_COMPARISON_ANALYTICS_TYPES = [
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_FLOATING,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_FLOATING,
] as const

export type AmberdataComparisonAnalyticsType = (typeof AMBERDATA_COMPARISON_ANALYTICS_TYPES)[number]

export function isAmberdataComparisonAnalyticsType(
  analyticsType: AnalyticsType
): analyticsType is AmberdataComparisonAnalyticsType {
  return AMBERDATA_COMPARISON_ANALYTICS_TYPES.includes(
    analyticsType as AmberdataComparisonAnalyticsType
  )
}

export function isAmberdataTermStructureAnalyticsType(analyticsType: AnalyticsType): boolean {
  return (
    analyticsType === AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_CONSTANT ||
    analyticsType === AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_FLOATING
  )
}

// Types that show from/to date pickers (time-series range)
export const AMBERDATA_DATE_RANGE_PICKER_TYPES = [
  AnalyticsType.ANALYTICS_TYPE_IMPLIED_VS_REALIZED,
  AnalyticsType.ANALYTICS_TYPE_PUT_CALL_RATIO,
  AnalyticsType.ANALYTICS_TYPE_SKEW_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_SKEW_FLOATING,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_RICHNESS,
  AnalyticsType.ANALYTICS_TYPE_USD_GAMMA,
  // Genuine time-series range with a real `limit` (row budget shared across
  // 3 curves) — same shape as USD Gamma.
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION_CUMULATIVE,
  // The three bar charts (DOW/HOUR/SESSION) always emit a fixed row count
  // (7/24/3) regardless of range — that's why they're also in
  // AMBERDATA_LIMIT_IGNORED_ANALYTICS_TYPES below (limit dropped from their
  // request) — but the *lookback window* used to compute each bucket's
  // aggregate still varies, so a from/to picker is exactly as meaningful here
  // as it is for the "genuine time-series" types above.
  AnalyticsType.ANALYTICS_TYPE_RETURNS_DOW,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_HOUR,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION,
] as const

export function isAmberdataDateRangePickerType(analyticsType: AnalyticsType): boolean {
  return AMBERDATA_DATE_RANGE_PICKER_TYPES.includes(
    analyticsType as (typeof AMBERDATA_DATE_RANGE_PICKER_TYPES)[number]
  )
}

// Phase 1 supported types (feeds with UI implementations)
export const PHASE_ONE_ANALYTICS_TYPES = [
  AnalyticsType.ANALYTICS_TYPE_VOL_METRICS_24HR,
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_FLOATING,
  AnalyticsType.ANALYTICS_TYPE_IMPLIED_VS_REALIZED,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_FLOATING,
  AnalyticsType.ANALYTICS_TYPE_PUT_CALL_RATIO,
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_RICHNESS,
  AnalyticsType.ANALYTICS_TYPE_SKEW_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_SKEW_FLOATING,
  // ENG-738 §7.2
  AnalyticsType.ANALYTICS_TYPE_USD_GAMMA,
  AnalyticsType.ANALYTICS_TYPE_GEX,
  AnalyticsType.ANALYTICS_TYPE_VOL_CONES,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_DOW,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_MOY,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_HOUR,
  // ENG-941 / oems#260
  AnalyticsType.ANALYTICS_TYPE_RETURNS_DOW,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_HOUR,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION_CUMULATIVE,
] as const

export type PhaseOneAmberdataAnalyticsType = (typeof PHASE_ONE_ANALYTICS_TYPES)[number]

export function isPhaseOneAmberdataAnalyticsType(
  analyticsType: AnalyticsType
): analyticsType is PhaseOneAmberdataAnalyticsType {
  return PHASE_ONE_ANALYTICS_TYPES.includes(analyticsType as PhaseOneAmberdataAnalyticsType)
}

export const AMBERDATA_ANALYTICS_PAYLOAD_KEYS = {
  [AnalyticsType.ANALYTICS_TYPE_VOL_METRICS_24HR]: 'volMetrics24h',
  [AnalyticsType.ANALYTICS_TYPE_PUT_CALL_RATIO]: 'putCallRatio',
  [AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_CONSTANT]: 'deltaSurfaceConstant',
  [AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_FLOATING]: 'deltaSurfaceFloating',
  [AnalyticsType.ANALYTICS_TYPE_IMPLIED_VS_REALIZED]: 'impliedVsRealized',
  [AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_CONSTANT]: 'termStructureConstant',
  [AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_FLOATING]: 'termStructureFloating',
  [AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_RICHNESS]: 'termStructureRichness',
  [AnalyticsType.ANALYTICS_TYPE_VOLATILITY_INDEX]: 'volatilityIndex',
  [AnalyticsType.ANALYTICS_TYPE_SKEW_CONSTANT]: 'skewConstant',
  [AnalyticsType.ANALYTICS_TYPE_SKEW_FLOATING]: 'skewFloating',
  [AnalyticsType.ANALYTICS_TYPE_USD_GAMMA]: 'usdGamma',
  [AnalyticsType.ANALYTICS_TYPE_GEX]: 'gex',
  [AnalyticsType.ANALYTICS_TYPE_VOL_CONES]: 'volCones',
  [AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_DOW]: 'rvSeasonalityDow',
  [AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_MOY]: 'rvSeasonalityMoy',
  [AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_HOUR]: 'rvSeasonalityHour',
  [AnalyticsType.ANALYTICS_TYPE_RETURNS_DOW]: 'bucketedReturns',
  [AnalyticsType.ANALYTICS_TYPE_RETURNS_HOUR]: 'bucketedReturns',
  [AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION]: 'bucketedReturns',
  [AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION_CUMULATIVE]: 'sessionCumulativeReturns',
} as const satisfies Partial<Record<AnalyticsType, keyof DataPayload>>

export type AmberdataAnalyticsPayloadKey =
  (typeof AMBERDATA_ANALYTICS_PAYLOAD_KEYS)[AmberdataAnalyticsType]

export type AmberdataAnalyticsList = NonNullable<DataPayload[AmberdataAnalyticsPayloadKey]>

export interface AmberdataAnalyticsRequestInput {
  symbol: string
  analyticsType: AmberdataAnalyticsType
  exchange?: Exchange
}

export function isAmberdataAnalyticsType(
  analyticsType: AnalyticsType
): analyticsType is AmberdataAnalyticsType {
  return AMBERDATA_ANALYTICS_TYPES.includes(analyticsType as AmberdataAnalyticsType)
}

export function isAmberdataRangeAnalyticsType(
  analyticsType: AnalyticsType
): analyticsType is AmberdataRangeAnalyticsType {
  return AMBERDATA_RANGE_ANALYTICS_TYPES.includes(analyticsType as AmberdataRangeAnalyticsType)
}

export function getAmberdataAnalyticsPayloadKey(
  analyticsType: AnalyticsType
): AmberdataAnalyticsPayloadKey | undefined {
  if (!isAmberdataAnalyticsType(analyticsType)) return undefined
  return AMBERDATA_ANALYTICS_PAYLOAD_KEYS[analyticsType]
}

export function extractAmberdataAnalyticsPayload(
  payload: DataPayload | undefined,
  analyticsType: AnalyticsType
): AmberdataAnalyticsList | undefined {
  const key = getAmberdataAnalyticsPayloadKey(analyticsType)
  if (!payload || !key) return undefined
  return payload[key] as AmberdataAnalyticsList | undefined
}

export function normalizeAmberdataSymbol(symbol: string, analyticsType: AnalyticsType): string {
  const normalized = symbol.trim().toUpperCase()

  if (analyticsType === AnalyticsType.ANALYTICS_TYPE_VOLATILITY_INDEX) {
    return normalized.startsWith('DVOL_') ? normalized : `DVOL_${normalized}`
  }

  return normalized.startsWith('DVOL_') ? normalized.slice('DVOL_'.length) : normalized
}

export function buildAmberdataAnalyticsRequest({
  exchange = DEFAULT_AMBERDATA_EXCHANGE,
  symbol,
  analyticsType,
}: AmberdataAnalyticsRequestInput) {
  return {
    exchange,
    symbol: normalizeAmberdataSymbol(symbol, analyticsType),
    analyticsType,
  }
}

// oems #277 follow-up — cost-optimization pass over the options-analytics widgets.

// Row-budgeted range endpoints: the server SAMPLE BYs down to a fixed bucket
// budget and ignores `limit` entirely (see the skew note above). Sending it is
// inert today, not actively harmful — but it's dead weight, and leaving it in
// makes it easy to reintroduce a real cap by accident if this contract ever
// changes back.
export const AMBERDATA_LIMIT_IGNORED_ANALYTICS_TYPES = [
  AnalyticsType.ANALYTICS_TYPE_SKEW_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_SKEW_FLOATING,
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_CONSTANT,
  AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_FLOATING,
  // Richness shares the same server-selected SAMPLE BY ladder and ignores limit.
  AnalyticsType.ANALYTICS_TYPE_TERM_STRUCTURE_RICHNESS,
  // PCR, Implied vs Realized and USD Gamma downsample too (options-analytics guide §6/§7.2):
  // ≤500 buckets picked from from/to, `limit` ignored, so one request covers the full
  // history back to 2019-04. They used to honour `limit`, which cut the start off long
  // windows (a 5000-row cap kept only the newest rows).
  AnalyticsType.ANALYTICS_TYPE_PUT_CALL_RATIO,
  AnalyticsType.ANALYTICS_TYPE_IMPLIED_VS_REALIZED,
  AnalyticsType.ANALYTICS_TYPE_USD_GAMMA,
  // The three bar charts always emit a fixed row set (7/24/3) regardless of
  // range — `limit` is accepted but unused server-side. Cumulative is NOT
  // here: it's a genuine time-series and `limit` caps its row budget.
  AnalyticsType.ANALYTICS_TYPE_RETURNS_DOW,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_HOUR,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION,
] as const

export function isAmberdataLimitIgnoredAnalyticsType(analyticsType: AnalyticsType): boolean {
  return AMBERDATA_LIMIT_IGNORED_ANALYTICS_TYPES.includes(
    analyticsType as (typeof AMBERDATA_LIMIT_IGNORED_ANALYTICS_TYPES)[number]
  )
}

// GEX/Vol Cones/RV Seasonality are snapshot-only AND low-cadence per the backend
// doc (GEX hourly upstream; cones/seasonality "recomputed snapshots stamped at
// poll time") — a standing WS subscription is a channel spent for data that
// rarely changes. VOL_METRICS_24HR is excluded: a rolling 24h table is
// plausibly meant to update live, unlike these three.
export const AMBERDATA_LOW_CADENCE_ANALYTICS_TYPES = [
  AnalyticsType.ANALYTICS_TYPE_GEX,
  AnalyticsType.ANALYTICS_TYPE_VOL_CONES,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_DOW,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_MOY,
  AnalyticsType.ANALYTICS_TYPE_RV_SEASONALITY_HOUR,
  // "Computed on read from the chart MVs; no producer, no snapshot" per the
  // generated enum doc on ANALYTICS_TYPE_RETURNS_DOW — no WS producer exists
  // for these, so there's no live channel to spend in the first place. Unlike
  // GEX/Vol Cones/RV Seasonality (snapshot-only, handled by
  // resolveAmberdataLowCadenceRefetchInterval feeding a snapshot poll),
  // these four are range types — the same resolver also feeds
  // useAmberdataAnalyticRange's refetchInterval (see use-amberdata-data.ts)
  // so they still get a bounded 15-min REST refresh instead of fetch-once.
  AnalyticsType.ANALYTICS_TYPE_RETURNS_DOW,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_HOUR,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION,
  AnalyticsType.ANALYTICS_TYPE_RETURNS_SESSION_CUMULATIVE,
] as const

export function isAmberdataLowCadenceAnalyticsType(analyticsType: AnalyticsType): boolean {
  return AMBERDATA_LOW_CADENCE_ANALYTICS_TYPES.includes(
    analyticsType as (typeof AMBERDATA_LOW_CADENCE_ANALYTICS_TYPES)[number]
  )
}

// Replaces the WS stream for the low-cadence types above with a bounded REST
// poll. This is a WS-channel-count reduction (the goal of this pass), NOT a
// claim that polling is cheaper in aggregate than one mostly-idle channel — it
// trades an open channel for a bounded-frequency request instead. 15 min is
// chosen conservatively against GEX's documented ~hourly upstream cadence (so
// a panel is stale by at most ~15 min, not ~60). Vol Cones/RV Seasonality have
// no published poll cadence in the backend doc — the same conservative
// interval is applied to them pending backend/product confirmation of their
// actual refresh rate; revisit if it turns out to be wastefully frequent (or
// not frequent enough) for either.
export const AMBERDATA_LOW_CADENCE_REFETCH_INTERVAL_MS = 15 * 60 * 1000

// Pure wiring functions (not just predicates) so the actual decision each call
// site makes is directly testable.
export function resolveAmberdataEnableStream(
  analyticsType: AnalyticsType,
  { isDateRangePicker, isLiveIvrRange }: { isDateRangePicker: boolean; isLiveIvrRange: boolean }
): boolean {
  if (isAmberdataLowCadenceAnalyticsType(analyticsType)) return false
  return !isDateRangePicker || isLiveIvrRange
}

// Named for the behavior (low-cadence types get a bounded REST poll instead
// of a WS channel), not the query kind — feeds both useAmberdataAnalyticSnapshot's
// and useAmberdataAnalyticRange's refetchInterval (see use-amberdata-data.ts).
export function resolveAmberdataLowCadenceRefetchInterval(
  analyticsType: AnalyticsType
): number | undefined {
  return isAmberdataLowCadenceAnalyticsType(analyticsType)
    ? AMBERDATA_LOW_CADENCE_REFETCH_INTERVAL_MS
    : undefined
}
